export interface Workflow {
  uId?: string;
  name?: string;
  description?: string;
  parentUId?: string;
  parentType?: WorkflowParentType;
  defaultWorkflowStepUId?: string;
  workflowSteps?: WorkflowStep[];
  operations?: WorkflowOperation;
  views?: View[];
  createdOn?: Date;
  updatedOn?: Date;
  lastEditedByName?: string;
  lastEditedById?: string;
  isActive?: boolean;
  isSystem?: boolean;
  isDefault?: boolean;
}

export interface WorkflowStep {
  uId?: string;
  sortOrder?: number;
  name?: string;
  workflowStepType?: WorkflowStepTypes;
  description?: string;
  workflowStatusUId?: string;
  statusName?: string;
  statusNameCulture?: string;
  statusHtmlColorCode?: string;
  actions?: WorkflowStepAction[];
  operations?: WorkflowOperation[];
  rules?: WorkflowStepRule[];
  translations?: WorkflowTranslation[];
  configuration?: any;
  createdOn?: Date;
  updatedOn?: Date;
  lastEditedByName?: string;
  lastEditedById?: string;
  teamMembers?: WorkflowStepTeamMember[];
}

export interface WorkflowTranslation {
  uId?: string;
  cultureCode: string;
  name: string;
}

export interface WorkflowStepConfiguration {
  initialStep?: boolean;
  completeStep?: boolean;
  required?: boolean;
  allTeamMembersAtOnce?: boolean;
}

export interface WorkflowStepAction {
  uId?: string;
  sortOrder?: number;
  name?: string;
  label?: string;
  description?: string;
  iconName?: string;
  actionType?: WorkflowStepActionTypes;
  destinationStepUId?: string;
  transitionViewUId?: string;
  configuration?: any;
  labelCultureCode?: string;
  labelTranslations?: WorkflowTranslation[];
}

export interface WorkflowStepTeamMember {
  uId?: string;
  workflowStepUId?: string;
  sortOrder?: number;
  memberUId?: string;
  memberDisplayName?: string;
  type?: WorkflowStepTeamMemberType;
  options?: WorkflowStepTeamMemberOptions;
  configuration?: any;
  createdOn?: Date;
  updatedOn?: Date;
  lastEditedByName?: string;
  lastEditedById?: string;
}

export interface WorkflowOperation {
  uId?: string;
  sortOrder?: number;
  workflowOperationType?: WorkflowOperationTypes;
  nextOperationUId?: string;
  executeOnEvent?: string;
  configuration?: any;
}

export interface WorkflowStatus {
  uId?: string | null;
  name?: string;
  htmlColorCode?: string;
  isActive?: boolean;
  cultureCode?: string;
  translations?: WorkflowTranslation[];
}

export interface WorkflowStatusView {
  datasetName?: string;
  statusName?: string;
}

export enum WorkflowStepTypes {
  Standard = 0,
  Approval = 1,
  Send = 2,
  Escalated = 3,
  Complete = 4,
  Paused = 5
}

export enum WorkflowStepActionTypes {
  NotSet = 0,
  Approve = 1,
  Reject = 2
}

export enum WorkflowOperationTypes {
  NotSet = 0,
  Save = 1,
  Delete = 2
}

export enum OperationEvents {
  NotSet = 0,
  Save = 1,
  Delete = 2
}

export enum WorkflowStepTeamMemberType {
  None = 0,
  User = 1,
  Group = 2
}

export enum WorkflowStepTeamMemberOptions {
  None = 0
}

export interface View {
  uId: string;
  name: string;
  description: string;
  controls: ViewComponent[];
  configuration: any;
}

export interface ViewComponent {
  uId?: string;
  viewUId?: string;
  parentViewComponentUId?: string;
  sortOrder?: number;
  name?: string;
  dataSetFieldUId?: string;
  controlType?: ViewControlType;
  configuration?: any;
  controls?: ViewComponent[];
}

export interface ViewContainer {
  controls?: ViewComponent[];
}

export enum ViewControlType {
  NotSet = 0,
  DatasetField = 1,
  EditDocumentButtonBar = 2,
  DocumentSummary = 3,
  WorkflowTransitionSelect = 4,
  AssetsImpacted = 5,
  Custom = 6,
  ViewControlContainer = 7,
  Section = 8
}

export enum CustomViewControlType {
  NotSet = 0,
  WorkflowSelection = 1
}

export interface WorkflowSelectionSettings {
  workflowStepListItems: WorkflowStep[];
  selectedStepUId: string;
  selectedStatusColorCode: string;
  isEdit: boolean;
  selectedStatusName: string;
  stepSelected: (WorkflowStep) => void;
  documentUpdated: (Document) => void;
}

export enum WorkflowRuleEvent {
  None = 0,
  StepStarted = 1,
  StepStopped = 2,
  DocumentUpdated = 3,
  AfterTime = 4,
  SpecificTime = 5,
  Scheduled = 6,
  Manual = 7,
  ProximityCalculated = 8,
  NotificationSent = 9,
  AfterTimeWithField = 10
}

export enum WorkflowRuleScheduleTrigger {
  None = 0,
  StepStarted = 1,
  StepStopped = 2,
  DocumentUpdated = 3
}

export interface WorkflowStepRule {
  uId?: string | null;
  name?: string | null;
  description?: string | null;
  sortOrder: number;
  ruleEvent: WorkflowRuleEvent;
  scheduleTrigger: WorkflowRuleScheduleTrigger;
  actions: WorkflowStepRuleAction[];
  displayText?: string | null;
  configuration?: string | null;
}

export interface WorkflowStepRuleAction {
  uId?: string | null;
  implementationUId?: string | null;
  sortOrder: number;
  displayText?: string | null;
  configuration?: string | null;
}

export interface WorkflowRuleImplementation {
  uId?: string | null;
  name?: string | null;
  description?: string | null;
}

export interface WorkflowRuleHistory {
  uId?: string | null;
  datasetUId: string | null;
  documentUId: string | null;
  workflowUId: string | null;
  profileUId: string | null;
  profileName: string | null;
  workflowStepUId: string | null;
  workflowStepStatus: string | null;
  workflowStepColor: string | null;
  workflowRuleUId: string | null;
  executionTime: string | null;
  status: WorkflowRuleHistoryStatus;
  workflowRuleDisplayText: string | null;
  message: string | null;
  actionsInfo: string | null;
}

export enum WorkflowRuleHistoryStatus {
  None = 0,
  StepLoop = 1
}

export class ViewNames {
  public static readonly DetailViewName = 'detail';
  public static readonly TableViewName = 'table';
  public static readonly CardViewName = 'card';
  public static readonly WizardViewName = 'wizard';
  public static readonly AnalyticsTableViewName = 'analytics-table';
}

export enum WorkflowParentType {
  None = 0,
  Dataset = 1,
  MassNotification = 2,
  MassNotificationRecipient = 3
}

export enum WorkflowHostType {
  None = 0,
  Document = 1,
  MassNotification = 2,
  MassNotificationRecipient = 3
}

// using AND between different field filters
export interface TableFilter {
  filters: TableFieldFilter[];
}

// using OR between different field values
export interface TableFieldFilter {
  systemName: string;
  values: { uId: string; name: string }[];
}
export interface WorkflowHost {
  uId?: string | null;
  workflowUId: string;
  workflowStepUId: string;
  hostType: WorkflowHostType;
}
